import * as _ from 'lodash';
import { IWaitListImportColumnMapping, IWaitListImportSampleRow } from '@nkr/models/index';
import { FormEvent, useState } from "react";
import { Alert, Button, Col, ControlLabel, Form, FormControl, FormGroup, Glyphicon, Label, Panel, Table } from "react-bootstrap";
import { FormWrapper, TableWrapper } from './styles';
import { PatientsNotInFile } from './patients-not-in-file';
import { ExistingMatch } from './existing-match';

interface IProps {
    headers: string[],
    sampleRows: IWaitListImportSampleRow[],
    columnMapping: IWaitListImportColumnMapping[],
    mappedFields: string[],
    hasColumnHeaders: boolean,
    autoOrderCheekSwabs: boolean,
    patientsNotInFile: PatientsNotInFile,
    existingMatch: ExistingMatch,
    onHasColumnHeadersChange: (hasColumnHeaders: boolean) => void,
    onAutoOrderCheekSwabsChange: (value: boolean) => void,
    onPatientsNotInFileChange: (value: PatientsNotInFile) => void,
    onExistingMatchChange: (value: ExistingMatch) => void,
    onMappedFieldsChange: (mappedFields: string[]) => void,
    onPreviousStep: () => void,
    onApply: () => void
}

const ColumnMapping = (props: IProps) => {
    const [sampleRowIndex, setSampleRowIndex] = useState(0);
    const [validation, setValidation] = useState(props.columnMapping.map(x => true));
    const [isDirty, setIsDirty] = useState(false);

    const handleAssignedColumnChange = (index: number, column: string) => {
        let newList = [...props.mappedFields];
        newList[index] = column;
        props.onMappedFieldsChange(newList);
    };

    const handleSampleRowChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        setSampleRowIndex(parseInt(e.currentTarget.value));
    };

    const handlePatientsNotInFileChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        props.onPatientsNotInFileChange(parseInt(e.currentTarget.value));
    };

    const handleExistingMatchChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        props.onExistingMatchChange(parseInt(e.currentTarget.value));
    };

    const handleApply = () => {
        let newValidation = props.columnMapping.map((cm, cmIndex) => {
            if (cm.isRequired) {
                return props.mappedFields[cmIndex] != '';
            } else {
                return true;
            }
        });

        let isValid = newValidation.filter(x => x == false).length == 0;

        setValidation(newValidation);
        setIsDirty(true);

        if (isValid) {
            props.onApply();
        }
    };

    const renderRequired = (isRequired: boolean, isValid: boolean) => {
        if (isRequired) {
            if (!isDirty) {
                return <Label bsStyle='warning'>Yes</Label>;
            } else {
                if (isValid) {
                    return <Label bsStyle='warning'>Yes</Label>;
                } else {
                    return <Label bsStyle='danger'>Yes</Label>;
                }
            }
        }

        return <>No</>;
    };

    if (props.sampleRows.length == 0)
        return null;

    const sampleRow = props.sampleRows[sampleRowIndex];
    const sortedHeaders = props.hasColumnHeaders ? _.orderBy(props.headers, header => header) : _.range(1, sampleRow.data.length + 1).map(f => f.toString());
    const isValid = validation.filter(x => x == false).length == 0;
    const requiredColumns = props.columnMapping.filter(x => x.isRequired);

    const upperCaseHeaders = props.headers.map(x => x.toUpperCase());

    return (
        <>
            <Panel>
                <Panel.Heading>Step 2: Map Required Columns</Panel.Heading>
                <Panel.Body>
                    <Alert bsStyle='warning' bsSize='sm'>
                        <span>The following columns are required:&nbsp;</span>
                        {requiredColumns.map((column, index) =>
                            <span key={'required-' + index}>{column.fieldName}{index < requiredColumns.length - 1 ? ', ' : ''}</span>
                        )}
                    </Alert>

                    {isDirty && !isValid && (
                        <Alert bsStyle='danger' bsSize='sm' style={{ paddingTop: 7.5, paddingBottom: 7.5 }}>
                            The items in <strong>red</strong> must be specified...
                        </Alert>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15, width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ marginRight: 5 }}>Does the first row contain column headers?</div>
                            <div>
                                <label className='radio-inline'>
                                    <input
                                        type='radio'
                                        id='hasColumnHeadersYes'
                                        name="hasColumnHeaders"
                                        checked={props.hasColumnHeaders}
                                        onClick={() => props.onHasColumnHeadersChange(true)}
                                    /> Yes
                                </label>
                                <label className='radio-inline'>
                                    <input
                                        type='radio'
                                        id='hasColumnHeadersNo'
                                        name="hasColumnHeaders"
                                        checked={!props.hasColumnHeaders}
                                        onClick={() => props.onHasColumnHeadersChange(false)}
                                    /> No
                                </label>
                            </div>
                        </div>
                    </div>

                    <TableWrapper condensed bordered fontSize='14px' style={{ marginBottom: 20 }}>
                        <thead>
                            <tr>
                                <th style={{ width: '30%', verticalAlign: 'middle' }}>Column From File</th>
                                <th style={{ verticalAlign: 'middle' }}>Required?</th>
                                <th style={{ verticalAlign: 'middle' }}>
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <div style={{ marginRight: 5 }}>Sample Data from Row</div>
                                        <FormControl
                                            componentClass='select'
                                            bsSize='sm'
                                            style={{ width: 'initial', padding: 0 }}
                                            value={sampleRowIndex}
                                            onChange={handleSampleRowChange}
                                        >
                                            {_.range(0, props.sampleRows.length).map(row =>
                                                <option value={row}>{row + 1}</option>
                                            )}
                                        </FormControl>
                                    </div>
                                </th>
                                <th style={{ verticalAlign: 'middle' }}>Map To</th>
                            </tr>
                        </thead>
                        <tbody>
                            {props.columnMapping.map((column, index) => {
                                let mappedField = props.mappedFields[index];
                                let valid = validation[index];
                                let sampleData = null;

                                if (mappedField != '') {
                                    sampleData = props.hasColumnHeaders ? sampleRow.data[upperCaseHeaders.indexOf(mappedField.toUpperCase())] : sampleRow.data[parseInt(mappedField) - 1];

                                    if (sampleData == '') {
                                        sampleData = <Label style={{ fontSize: '90%' }}>No Sample Data</Label>;
                                    }
                                } else {
                                    sampleData = <Label style={{ fontSize: '90%' }}>Pending Selection =&gt;</Label>;
                                }

                                return (
                                    <tr>
                                        <td>{column.fieldName}</td>
                                        <td>{renderRequired(column.isRequired, valid)}</td>
                                        <td>{sampleData}</td>
                                        <td>
                                            <FormGroup style={{ marginBottom: 0 }} validationState={isDirty && !valid ? 'error' : null}>
                                                <FormControl
                                                    componentClass='select'
                                                    bsSize='sm'
                                                    value={mappedField.toUpperCase()}
                                                    onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => handleAssignedColumnChange(index, e.currentTarget.value)}
                                                >
                                                    <option value=''>- Assign Column -</option>
                                                    {sortedHeaders.map(header =>
                                                        <option value={header.toUpperCase()}>{header}</option>
                                                    )}
                                                </FormControl>
                                            </FormGroup>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </TableWrapper>

                    <FormWrapper>
                        <FormGroup>
                            <Col xs={12} lg={4} componentClass={ControlLabel}>Auto Order Cheek Swabs?</Col>
                            <Col xs={12} lg={6}>
                                <label className='radio-inline'>
                                    <input
                                        type='radio'
                                        id='autoOrderCheekSwabsYes'
                                        name="autoOrderCheekSwabs"
                                        checked={props.autoOrderCheekSwabs}
                                        onClick={() => props.onAutoOrderCheekSwabsChange(true)}
                                    /> Yes
                                </label>
                                <label className='radio-inline'>
                                    <input
                                        type='radio'
                                        id='autoOrderCheekSwabsNo'
                                        name="autoOrderCheekSwabs"
                                        checked={!props.autoOrderCheekSwabs}
                                        onClick={() => props.onAutoOrderCheekSwabsChange(false)}
                                    /> No
                                </label>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={4} componentClass={ControlLabel}>What should we do with Waitlist Patients<br />in the NKR system that are NOT in this file?</Col>
                            <Col xs={12} lg={6}>
                                <FormControl
                                    componentClass='select'
                                    style={{ width: 'initial' }}
                                    value={props.patientsNotInFile}
                                    onChange={handlePatientsNotInFileChange}
                                >
                                    <option value={PatientsNotInFile.Ignore}>Ignore Them</option>
                                    <option value={PatientsNotInFile.Deactivate}>Deactivate Them</option>
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={4} componentClass={ControlLabel}>What should we do if the MRN in the file<br />matches an existing record?</Col>
                            <Col xs={12} lg={6}>
                                <FormControl
                                    componentClass='select'
                                    style={{ width: 'initial' }}
                                    value={props.existingMatch}
                                    onChange={handleExistingMatchChange}
                                >
                                    <option value={ExistingMatch.Skip}>Skip Entry</option>
                                    <option value={ExistingMatch.Update}>Update Entry</option>
                                </FormControl>
                            </Col>
                        </FormGroup>
                    </FormWrapper>
                </Panel.Body>
                <Panel.Footer className='text-right'>
                    <Button bsStyle='link' onClick={props.onPreviousStep}><Glyphicon glyph='chevron-left' /> Previous Step</Button>&nbsp;
                    <Button bsStyle='primary' onClick={handleApply}>Apply Column Mapping to File</Button>
                </Panel.Footer>
            </Panel>
        </>
    );
}

export default ColumnMapping;